import { useMemo } from 'react'
import { Permission, useUserPermissionGrantsQuery } from '../../../graphql'

export type CancelTransferOrderPermission = {
	/** The viewer holds the cancel permission for the order's `from_store`. */
	canCancel: boolean,
	/**
	 * The viewer holds it for `to_store` but not `from_store` — the receiving-side
	 * case the API rejects. Surfaces the disabled Cancel action plus its
	 * explanatory tooltip, rather than silently hiding the action from someone who
	 * reasonably expects to have it.
	 */
	blockedAsReceiver: boolean,
}

export const NO_CANCEL_PERMISSION: CancelTransferOrderPermission = { canCancel: false, blockedAsReceiver: false }

/**
 * The store ids the viewer holds `CAN_CANCEL_PARTS_TRANSFER_BY_STORE_ID` for,
 * out of `storeIds`.
 *
 * `storeIds` goes to the server as the `objectIds` filter, matching every other
 * store-scoped caller of `userPermissionGrants` — an unfiltered request returns
 * every grant the user holds, which is large for admin-level accounts. The ids
 * are sorted and de-duplicated so the variables, and therefore the Apollo cache
 * key, depend only on which stores are asked about, not on row order.
 *
 * The `Set` is built once per response rather than once per lookup, so callers
 * resolving many transfer orders against it pay O(1) each.
 *
 * `granted` is empty while the query is in flight, which reads as "no
 * permission yet" — the Cancel action stays hidden until the answer arrives.
 */
export function useCancelGrants( storeIds: ( string | null | undefined )[] ): { granted: Set<string>, loading: boolean } {
	const objectIds = useMemo(
		() => Array.from( new Set( storeIds.filter( ( id ): id is string => !!id ) ) ).sort( ( a, b ) => a.localeCompare( b ) ),
		// Keyed on the joined ids, not the array, so a caller passing a fresh array
		// with the same ids each render does not produce new query variables.
		[ storeIds.join( ',' ) ],
	)

	const { data, loading } = useUserPermissionGrantsQuery( {
		variables: {
			permissions: [ Permission.CanCancelPartsTransferByStoreId ],
			objectIds,
		},
		skip: objectIds.length === 0,
	} )

	const granted = useMemo( () => new Set(
		( loading ? [] : data?.userPermissionGrants ?? [] )
			.filter( ( grant ) => grant.permission === Permission.CanCancelPartsTransferByStoreId )
			.flatMap( ( grant ) => grant.objectIds.map( ( objectId ) => String( objectId ) ) )
	), [ data, loading ] )

	return { granted, loading }
}

/**
 * Whether the viewer may cancel one transfer order, given the stores they hold
 * the cancel permission for.
 *
 * Cancelling a transfer order is a sending-side action for every sender:
 * `from_store` still owns the outstanding inventory, so `CancelTransferOrder` /
 * `CancelTransferOrderLineItem` in inventory-api check
 * `CAN_CANCEL_PARTS_TRANSFER_BY_STORE_ID` against `from_store` alone. The
 * receiving store's lever is receiving (or declining to receive), not
 * cancelling. Whether the sender is a warehouse only changes the explanation
 * shown — see `cancelRestrictedTooltip`.
 */
export function resolveCancelPermission(
	granted: Set<string>,
	fromStoreId?: string | null,
	toStoreId?: string | null,
): CancelTransferOrderPermission {
	if ( !fromStoreId ) {
		return NO_CANCEL_PERMISSION
	}

	const canCancel = granted.has( fromStoreId )

	return {
		canCancel,
		blockedAsReceiver: !canCancel && !!toStoreId && granted.has( toStoreId ),
	}
}

/**
 * `resolveCancelPermission` for a single transfer order, such as
 * `TransferOrderDetails`.
 *
 * The transfer-orders grid does not use this: it renders one menu per row, and a
 * per-row query would be one request per row. `useTransferOrdersDataQuery` asks
 * once for every store on the page and attaches the answer to each row instead.
 */
export default function useCanCancelTransferOrder(
	fromStoreId?: string | null,
	toStoreId?: string | null,
): CancelTransferOrderPermission {
	const { granted } = useCancelGrants( [ fromStoreId, toStoreId ] )

	return resolveCancelPermission( granted, fromStoreId, toStoreId )
}
